import { SidebarInset, SidebarProvider, SidebarTrigger } from '@repo/ui'
import { useRouter, useRouterState } from '@tanstack/react-router'
import { createContext, useContext, useEffect, useState } from 'react'
import type { OrgUser } from '@/api/org/users'
import type { ProjectApiResponse } from '@/api/project/list'
import type { UserOrgProfile } from '@/api/user/profile'
import { getProposal } from '@/api/proposals/proposals'
import type { Organization } from '@/components/AppSidebar'
import type { ConversationCollectionKind } from '@/features/conversation/ConversationShell'
import { CanvasOutlineProvider } from '@/features/conversation/canvasOutline'
import { ScopedSettingsDialogHost } from '@/features/settings/ScopedSettingsDialogHost'
import { useScopedSettings } from '@/features/settings/ScopedSettings'
import {
  canvasIdentity,
  canvasSearchKeys,
  draftChat,
  switchChatSearch
} from '@/routes/_auth/$orgSlug/$projectId/workspace/-search'
import { ConversationCommandMenu } from './ConversationCommandMenu'
import { ConversationRail } from './ConversationRail'
import { useConversationShell } from './useConversationShell'
import type { ReactNode } from 'react'
import type { ConversationShellController } from './useConversationShell'

const ConversationWorkspaceShellControllerContext = createContext<ConversationShellController | undefined>(undefined)

export function useConversationWorkspaceShellController(): ConversationShellController {
  const controller = useContext(ConversationWorkspaceShellControllerContext)
  if (controller === undefined) throw new Error('Conversation workspace shell is unavailable.')
  return controller
}

// The workspace owns the path and the open chat is search state, so the rail reads the
// project from the URL and the chat from the query. The legacy `/chat/<id>` form is still
// matched because its redirect has not run yet on the first render after a cold load.
function routeScope(
  pathname: string,
  searchStr: string
): {
  projectId?: number
  conversationId?: number | 'new'
  collection?: ConversationCollectionKind
  canvasOpen: boolean
} {
  const match = pathname.match(/^\/[^/]+\/(\d+)(?:\/chat\/(\d+))?/)
  const projectId = match?.[1] === undefined ? undefined : Number(match[1])
  const legacyConversationId = match?.[2] === undefined ? undefined : Number(match[2])
  const search = new URLSearchParams(searchStr)
  const searchChat = search.get('chat')
  const scope: {
    projectId?: number
    conversationId?: number | 'new'
    collection?: ConversationCollectionKind
    canvasOpen: boolean
  } = { canvasOpen: false }
  if (projectId !== undefined && Number.isSafeInteger(projectId) && projectId > 0) scope.projectId = projectId
  // The open canvas is the URL's too, and the rail marks the row it belongs to. Reading
  // it through the same identity the loaders use keeps one answer to what `doc` names.
  const canvas = canvasIdentity({ doc: search.get('doc') })
  scope.canvasOpen = canvas !== undefined
  if (canvas?.canvas === 'collection') scope.collection = canvas.collection
  // The draft has no identifier, so it is carried through as itself. The legacy path
  // form never named one, which is why only the search value can be `new`.
  if (legacyConversationId === undefined && searchChat === draftChat) {
    scope.conversationId = draftChat
    return scope
  }
  const conversationId = legacyConversationId ?? (searchChat === null ? undefined : Number(searchChat))
  if (conversationId !== undefined && Number.isSafeInteger(conversationId) && conversationId > 0) {
    scope.conversationId = conversationId
  }
  return scope
}

export function resolvedConversationRailOpen(
  preferredOpen: boolean,
  canvasOpen: boolean,
  expandedOverCanvas: boolean
): boolean {
  return canvasOpen && !expandedOverCanvas ? false : preferredOpen
}

export function ConversationWorkspaceShell({
  orgId,
  currentOrgSlug,
  organizations,
  orgUsers,
  userOrgProfile,
  projects,
  children
}: {
  orgId: number
  currentOrgSlug: string
  organizations: Array<Organization>
  orgUsers: Array<OrgUser>
  userOrgProfile: UserOrgProfile
  projects: Array<ProjectApiResponse>
  children: ReactNode
}) {
  const router = useRouter()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const routeSearch = useRouterState({ select: (state) => state.location.searchStr })
  const scope = routeScope(pathname, routeSearch)
  const controller = useConversationShell(orgId, scope.projectId)
  const { openSettings } = useScopedSettings()
  const [preferredRailOpen, setPreferredRailOpen] = useState(true)
  const [railExpandedOverCanvas, setRailExpandedOverCanvas] = useState(false)
  const railOpen = resolvedConversationRailOpen(preferredRailOpen, scope.canvasOpen, railExpandedOverCanvas)
  const routeSynchronizedController: ConversationShellController = {
    ...controller,
    renameChat: async (projectId, conversationId, title) => {
      const renamed = await controller.renameChat(projectId, conversationId, title)
      if (renamed !== undefined) await router.invalidate()
      return renamed
    },
    archiveChat: async (projectId, conversationId) => {
      const archived = await controller.archiveChat(projectId, conversationId)
      if (archived !== undefined) await router.invalidate()
      return archived
    },
    restoreChat: async (projectId, conversationId) => {
      const restored = await controller.restoreChat(projectId, conversationId)
      if (restored !== undefined) await router.invalidate()
      return restored
    }
  }
  const [commandOpen, setCommandOpen] = useState(false)
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLocaleLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) return
      event.preventDefault()
      setCommandOpen((open) => !open)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Opening a chat leaves the canvas alone when it is the same workspace: the document
  // is the workspace's, not the chat's, so a chat switch has no business closing it.
  const openChat = (projectId: number, conversationId: number | typeof draftChat) =>
    void router.navigate({
      to: '/$orgSlug/$projectId/workspace',
      params: { orgSlug: currentOrgSlug, projectId: String(projectId) },
      search: (current: Record<string, unknown>) =>
        projectId === scope.projectId ? switchChatSearch(current, conversationId) : { chat: conversationId }
    })
  const openWorkspace = (projectId: number) =>
    void router.navigate({
      to: '/$orgSlug/$projectId/workspace',
      params: { orgSlug: currentOrgSlug, projectId: String(projectId) },
      search: {}
    })
  const openCanvas = (projectId: number, doc: Record<string, string | number>) =>
    router.navigate({
      to: '/$orgSlug/$projectId/workspace',
      params: { orgSlug: currentOrgSlug, projectId: String(projectId) },
      search: (current: Record<string, unknown>) => {
        const rest = { ...current }
        for (const key of canvasSearchKeys) delete rest[key]
        return { ...rest, ...doc }
      }
    })
  const openCollection = (kind: ConversationCollectionKind) => {
    if (scope.projectId === undefined) return
    void openCanvas(scope.projectId, { doc: `collection:${kind}` })
  }
  const openArtifact = async (
    projectId: number,
    kind: 'proposal' | 'presentation' | 'document',
    artifactId: number,
    position?: { section: number } | { slide: number; version: number }
  ) => {
    if (scope.projectId !== projectId) return
    // A document opens whole. Its revisions are a list inside the canvas rather than a
    // position in the URL, because a reader opening one from the rail wants what it says
    // now, not the revision some earlier link happened to name.
    if (kind === 'document') {
      await openCanvas(projectId, { doc: `document:${artifactId}` })
      return
    }
    if (kind === 'presentation') {
      await openCanvas(projectId, {
        doc: `presentation:${artifactId}`,
        ...(position !== undefined && 'slide' in position ? position : {})
      })
      return
    }
    if (position !== undefined && 'section' in position) {
      await openCanvas(projectId, { doc: `proposal:${artifactId}`, section: position.section })
      return
    }
    try {
      const proposal = await getProposal(projectId, artifactId)
      const section = [...proposal.sections].sort(
        (left, right) => left.ordinal - right.ordinal || left.sectionId - right.sectionId
      )[0]
      if (proposal.projectId !== projectId || section === undefined) throw new Error('Proposal is unavailable.')
      await openCanvas(projectId, { doc: `proposal:${artifactId}`, section: section.sectionId })
    } catch {
      openCollection('proposals')
    }
  }
  // Nothing is created here. The draft is a URL, and the chat behind it is written by
  // the first message sent into it, so an unused one costs the reader nothing.
  const openDraft = (projectId: number) => openChat(projectId, draftChat)

  return (
    <ConversationWorkspaceShellControllerContext.Provider value={routeSynchronizedController}>
      {/*
        The provider wraps both the rail and the outlet, which is what lets the open
        canvas hand its outline to the rail: the rail becomes the document's navigation
        without a second reader of the same artifact.
      */}
      <CanvasOutlineProvider>
        <SidebarProvider
          open={railOpen}
          onOpenChange={(open) => {
            setPreferredRailOpen(open)
            if (!open) setRailExpandedOverCanvas(false)
            else if (scope.canvasOpen) setRailExpandedOverCanvas(true)
          }}
        >
          <ConversationRail
            controller={routeSynchronizedController}
            organizations={organizations}
            currentOrgSlug={currentOrgSlug}
            orgUsers={orgUsers}
            {...(scope.projectId === undefined ? {} : { selectedProjectId: scope.projectId })}
            {...(scope.conversationId === undefined ? {} : { selectedConversationId: scope.conversationId })}
            {...(scope.collection === undefined ? {} : { selectedCollection: scope.collection })}
            onOpenChat={openChat}
            onOpenWorkspace={openWorkspace}
            onOpenCollection={openCollection}
            onOpenArtifact={(projectId, kind, artifactId, position) =>
              void openArtifact(projectId, kind, artifactId, position)
            }
            onOpenPreviousWork={(projectId, kind, threadId) =>
              void router.navigate({
                to:
                  kind === 'search'
                    ? '/$orgSlug/$projectId/search-agent/$threadId'
                    : '/$orgSlug/$projectId/marketing-agent/$threadId',
                params: {
                  orgSlug: currentOrgSlug,
                  projectId: String(projectId),
                  threadId: String(threadId)
                }
              })
            }
            onOpenCommandSearch={() => setCommandOpen(true)}
            onOpenOrganisationSettings={() => openSettings({ scope: 'organisation', section: 'account' })}
            onOpenWorkspaceSettings={(projectId) => openSettings({ scope: 'workspace', projectId, section: 'general' })}
          />
          <SidebarTrigger floating />
          <ConversationCommandMenu
            open={commandOpen}
            {...(routeSynchronizedController.shell === undefined ? {} : { shell: routeSynchronizedController.shell })}
            {...(scope.projectId === undefined ? {} : { selectedProjectId: scope.projectId })}
            onOpenChange={setCommandOpen}
            onOpenChat={openChat}
            onOpenWorkspace={openWorkspace}
            onOpenCollection={openCollection}
            onOpenArtifact={(projectId, kind, artifactId) => void openArtifact(projectId, kind, artifactId)}
            onStartChat={openDraft}
          />
          <ScopedSettingsDialogHost
            userOrgProfile={userOrgProfile}
            projects={projects}
            orgUsers={orgUsers}
            onAuthorityChanged={async () => {
              await router.invalidate()
              controller.retry()
            }}
          />
          <SidebarInset className="min-w-0 overflow-hidden bg-background">{children}</SidebarInset>
        </SidebarProvider>
      </CanvasOutlineProvider>
    </ConversationWorkspaceShellControllerContext.Provider>
  )
}
